import { useCallback } from 'react'
import gql from 'graphql-tag'
import type { Asset } from '@ztechapps/common.contract'
import type { UseMutationProps } from '../useMutation'
import { useMutation } from '../useMutation'
import type { AssetEntityType } from './useCreateAsset'

/**
 * Arguments for the delete asset mutation.
 * The filter must include both the entity _id and the asset _id.
 */
export type DeleteAssetArgs = {
  filter: { _id: string; 'assets._id': string }
}

/**
 * Result type for useDeleteAsset hook
 */
export type UseDeleteAssetResult = [
  (args: DeleteAssetArgs) => Promise<{ _id: string; assets: Asset[] } | undefined>,
  { error: any; loading: boolean },
]

/**
 * Builds the GraphQL mutation query for deleting an asset from a specific entity type.
 * Uses child resolver pattern: delete{EntityType}_assets
 */
const buildDeleteAssetQuery = (entityType: AssetEntityType) => {
  const mutationName = `delete${entityType}_assets`

  const queryString = `
    mutation ${mutationName}($filter: JSON!) {
      ${mutationName}(filter: $filter) {
        _id
        assets {
          _id
          url
          title
          subtitle
          type
          thumbUrl
          mediumUrl
          largeUrl
        }
      }
    }
  `

  return gql(queryString)
}

/**
 * Hook for deleting an asset from an entity using child resolver mutations.
 *
 * Uses the pattern delete{EntityType}_assets which:
 * - Removes the specific asset by ID
 * - Provides atomic updates avoiding race conditions
 * - Is more efficient than filtering and updating the entire array
 *
 * @param entityType - The entity type (Product, Order, Task, Project, PurchaseOrder)
 * @param props - Optional mutation configuration
 * @returns Tuple of [executeFunction, { error, loading }]
 *
 * @example
 * ```tsx
 * const [deleteAsset, { loading }] = useDeleteAsset('Product')
 *
 * await deleteAsset({
 *   filter: { _id: productId, 'assets._id': assetId }
 * })
 * ```
 */
export const useDeleteAsset = (
  entityType: AssetEntityType,
  { query, ...props } = {} as UseMutationProps
): UseDeleteAssetResult => {
  const defaultQuery = buildDeleteAssetQuery(entityType)

  const [executeMutation, , { error, loading }] = useMutation<{ _id: string; assets: Asset[] }>({
    query: query ?? defaultQuery,
    ...props,
  })

  // Wrap the mutation to properly extract filter from DeleteAssetArgs
  const executeDelete = useCallback(
    async (args: DeleteAssetArgs) => {
      const result = await executeMutation({
        filter: args.filter,
      })
      return result?.data ? (Object.values(result.data)[0] as unknown as { _id: string; assets: Asset[] }) : undefined
    },
    [executeMutation]
  )

  return [executeDelete, { error, loading }]
}
